/**
 * Audit System Internationalization - English
 */
export const auditEn = {
  title: 'Audit Logs',
  refresh: 'Refresh',
  soxCompliance: 'SOX Compliance',
  
  // Navigation
  nav: {
    overview: 'Overview',
    logs: 'Audit Logs',
    search: 'Advanced Search',
    statistics: 'Statistics',
    financial: 'Financial Audit',
    sensitive: 'Sensitive Operations',
    integrity: 'Integrity Verification',
  },

  // Relative time labels (consumed by formatRelativeTime util)
  relativeTime: {
    justNow: 'Just now',
    minutesAgo: '{n}m ago',
    hoursAgo: '{n}h ago',
    daysAgo: '{n}d ago',
  },

  // Overview
  overview: {
    title: 'Audit Overview',
    description: 'Monitor system operations, ensure compliance and security',
    lastSevenDays: 'Last 7 Days',
    failedOperations: 'Failed',
    moduleDistribution: 'Module Distribution',
    actionTypes: 'Action Types',
    dailyTrend: 'Daily Trend',
    recentActivity: 'Recent Activity',
    viewAll: 'View All',
    noData: 'No data',
    noLogs: 'No audit logs',
    loadFailed: 'Failed to Load',
    retry: 'Retry',
    verifyIntegrity: 'Verify',
    soxComplianceDesc: 'Encrypted storage and integrity verification enabled',
  },
  
  // Logs List
  logs: {
    title: 'Audit Logs',
    description: 'View and search system operation logs',
    totalRecords: '{count} records',
    perPage: 'Per page',
    records: 'records',
    searchPlaceholder: 'Search operations, usernames...',
    advancedFilters: 'Advanced Filters',
    clearFilters: 'Clear Filters',
    export: 'Export',
    exportCSV: 'Export CSV',
    exporting: 'Exporting...',
  },
  
  // Log Detail
  detail: {
    title: 'Audit Log Detail',
    basicInfo: 'Basic Information',
    changeDetails: 'Change Details',
    contextInfo: 'Context Information',
    integrityInfo: 'Integrity Information',
    oldValue: 'Before',
    newValue: 'After',
    changes: 'Changes',
    changeDetail: 'Change Details',
    noChanges: 'No changes recorded',
    backToList: 'Back to List',
    back: 'Back',
    retry: 'Retry',
    loadFailed: 'Load Failed',
    logNotFound: 'Log not found',
    operationOverview: 'Operation Overview',
    sensitive: 'Sensitive',
    financial: 'Financial',
    who: 'Who',
    what: 'What',
    when: 'When',
    where: 'Where',
    how: 'How',
    why: 'Why',
    module: 'Module',
    status: 'Status',
    riskLevel: 'Risk Level',
    operationDesc: 'Description',
    targetEntity: 'Target Entity',
    entityType: 'Entity Type',
    entityId: 'Entity ID',
    userId: 'User ID',
    sessionId: 'Session ID',
    traceId: 'Trace ID',
    requestId: 'Request ID',
    ipAddress: 'IP Address',
    userAgent: 'User Agent',
    contentHash: 'Content Hash',
    previousHash: 'Previous Hash',
    chainIndex: 'Chain Index',
    createdAt: 'Created At',
    archivedAt: 'Archived At',
    copySuccess: 'Copied',
    actionType: 'Action Type',
    complianceLevel: 'Compliance Level',
    retentionYears: 'Retention Years',
    years: 'years',
    duration: 'Duration',
    errorMessage: 'Error Message',
    deviceId: 'Device ID',
    businessType: 'Business Type',
    businessKey: 'Business Key',
    region: 'Region',
    tenantId: 'Tenant ID',
    currentHash: 'Current Hash',
    signature: 'Signature',
    geoLocation: 'Geo Location',
    deviceType: 'Device Type',
    deviceTypeDesktop: 'Desktop',
    deviceTypeMobile: 'Mobile',
    operatingSystem: 'Operating System',
    browser: 'Browser',
    operationSource: 'Source',
    viewSource: 'View Source Record',
    verifyIntegrity: 'Verify Integrity',
    verifying: 'Verifying...',
    integrityValid: '✓ Untampered',
    integrityInvalid: '✗ Tampered',
    integrityVerifyFailed: 'Verification failed',
    integrityTamperedHint: 'Stored hash does not match recomputed hash; record may have been tampered with',
    diff: {
      summary: '{count} change(s)',
      unchangedSuffix: ', {count} unchanged',
      showUnchanged: 'Show unchanged',
      hideUnchanged: 'Hide unchanged',
      noChanges: 'No field changes',
      tagAdded: 'Added',
      tagRemoved: 'Removed',
      tagModified: 'Modified',
      tagUnchanged: 'Unchanged',
    },
  },
  
  // Advanced Search
  search: {
    title: 'Advanced Search',
    description: 'Search audit records by entity, user or trace ID',
    entityHistory: 'Entity History',
    userOperations: 'User Operations',
    traceChain: 'Trace Chain',
    entityType: 'Entity Type',
    entityId: 'Entity ID',
    userId: 'User ID',
    traceId: 'Trace ID',
    search: 'Search',
    searching: 'Searching...',
    invalidUUID: '⚠️ Invalid UUID format',
    validFormat: '✓ Valid format',
    uuidHint: 'Please enter standard UUID format (36 characters with hyphens)',
    entityHistoryTitle: 'Entity Operation History',
    userSummary: 'User Summary',
    totalOperations: 'Total Operations',
    sensitiveOperations: 'Sensitive',
    financialOperations: 'Financial',
    failedOperations: 'Failed',
    operationRecords: 'Operation Records',
    traceChainTitle: 'Request Trace',
    totalDuration: 'Total Duration',
    required: '*',
    entityTypePlaceholder: 'e.g. User / users / Document (case-insensitive)',
    entityIdPlaceholder: '550e8400-e29b-41d4-a716-446655440000',
    entityHistoryDesc: 'Query all operation history for a specific entity, including create, update, delete, etc.',
    userOperationsDesc: 'Query all operation records for a specific user, including operation statistics',
    traceChainDesc: 'Query the complete call chain for a single request, tracking cross-service calls',
    pleaseEnterEntityTypeAndId: 'Please enter entity type and entity ID',
    pleaseEnterUserId: 'Please enter user ID',
    pleaseEnterTraceId: 'Please enter trace ID',
    invalidUUIDFormat: 'Must be a valid UUID format (e.g.: 550e8400-e29b-41d4-a716-446655440000)',
    searchFailed: 'Search failed',
    totalRecords: '{count} total records',
    changedFields: 'Changed fields',
  },
  
  // Statistics
  statistics: {
    title: 'Statistics',
    description: 'Audit log statistics and trend analysis',
    moduleDistribution: 'Module Distribution',
    actionTypes: 'Action Types',
    actionDistribution: 'Action Type Distribution',
    dailyTrend: 'Daily Trend',
    topUsers: 'Top Users',
    successRate: 'Success Rate',
    timeRange: 'Time Range',
    to: 'to',
    quickSelect: 'Quick',
    last7Days: 'Last 7 days',
    last30Days: 'Last 30 days',
    last90Days: 'Last 90 days',
    totalOperations: 'Total Operations',
    successOperations: 'Success Operations',
    failedOperations: 'Failed Operations',
    financialOperations: 'Financial Operations',
    sensitiveOperations: 'Sensitive Operations',
    loadFailed: 'Load Failed',
    loadingStatistics: 'Failed to load statistics data',
  },
  
  // Financial Audit
  financial: {
    title: 'Financial Operations',
    description: 'View financial audit logs',
    retentionYears: 'Retain {years} years',
    soxCompliance: 'SOX Compliance',
    soxComplianceDesc: 'Financial operation logs are retained for 7 years, supporting integrity verification and audit traceability',
    timeRangeRequired: 'Time Range (Required, max 1 year)',
    last7Days: 'Last 7 days',
    last1Month: 'Last 1 month',
    last3Months: 'Last 3 months',
    advancedFilters: 'Advanced Filters',
    clearFilters: 'Clear',
    enterModuleName: 'Enter module name',
    enterUserId: 'Enter user ID',
    statisticsSummary: 'Statistics Summary',
    totalRecords: 'Total Records',
    actionDistribution: 'Action Type Distribution',
    moduleDistribution: 'Module Distribution',
    loadFailed: 'Failed to load financial audit logs',
  },
  
  // Sensitive Operations
  sensitive: {
    title: 'Sensitive Operations',
    description: 'View high-risk and sensitive operation logs',
    mfaVerified: 'MFA Verified',
    requiresApproval: 'Requires Approval',
    securityMonitoring: 'Security Monitoring',
    securityDesc: 'Sensitive operations include password changes, permission adjustments, data deletion and other high-risk operations that require special attention',
    totalSensitiveOps: 'Total Sensitive Operations',
    last7Days: 'Last 7 days',
    last1Month: 'Last 1 month',
    advancedFilters: 'Advanced Filters',
    clearFilters: 'Clear',
    enterUserId: 'Enter user ID',
    totalSensitiveRecords: '{count} total sensitive operation records',
    loadFailed: 'Failed to load sensitive operation logs',
  },
  
  // Integrity Verification
  integrity: {
    title: 'Integrity Verification',
    description: 'Verify audit log integrity and immutability',
    verify: 'Verify Integrity',
    verifying: 'Verifying...',
    verified: 'Verified',
    failed: 'Failed',
    totalRecords: 'Total Records',
    passCount: 'Passed',
    failCount: 'Failed',
    failures: 'Failures',
    verifiedAt: 'Verified At',
    duration: 'Duration',
    startVerification: 'Start Verification',
    selectTimeRange: 'Select Time Range',
    hashChainIntegrity: 'Hash Chain Integrity',
    hashChainDesc: 'Audit logs use SHA-256 hash chain technology to ensure data integrity. Each log contains the hash value of the previous log, forming an immutable chain structure. The verification process checks whether the hash value of each record is correct to detect any data tampering.',
    immediateVerification: 'Immediate Verification',
    immediateVerificationDesc: 'Synchronously verify logs within a specified time range',
    startDate: 'Start Date',
    endDate: 'End Date',
    recommendedTimespan: 'Recommended time span no more than 30 days to avoid timeout',
    backgroundCheck: 'Background Check',
    backgroundCheckDesc: 'Asynchronously execute large-scale integrity checks',
    checkScope: 'Check Scope',
    allLogs: 'All Logs',
    queueing: 'Queueing',
    executing: 'Executing',
    completed: 'Completed',
    triggerCheckFailed: 'Failed to trigger check',
  },
  
  // Filters
  filters: {
    actionType: 'Action Type',
    status: 'Status',
    riskLevel: 'Risk Level',
    module: 'Module',
    startDate: 'Start Date',
    endDate: 'End Date',
    onlyFinancial: 'Financial Only',
    onlySensitive: 'Sensitive Only',
    all: 'All',
  },
  
  // Action Types
  action: {
    create: 'Create',
    read: 'Read',
    update: 'Update',
    delete: 'Delete',
    bulkUpdate: 'Bulk Update',
    bulkDelete: 'Bulk Delete',
    approve: 'Approve',
    reject: 'Reject',
    return: 'Return',
    forward: 'Forward',
    withdraw: 'Withdraw',
    login: 'Login',
    logout: 'Logout',
    loginFailed: 'Login Failed',
    passwordChange: 'Password Change',
    passwordReset: 'Password Reset',
    emailChange: 'Email Change',
    permissionChange: 'Permission Change',
    roleChange: 'Role Change',
    configChange: 'Config Change',
    backup: 'Backup',
    restore: 'Restore',
    export: 'Export',
    import: 'Import',
    deploy: 'Deploy',
    rollback: 'Rollback',
    payment: 'Payment',
    refund: 'Refund',
    invoiceGenerate: 'Invoice Generate',
    financialClose: 'Financial Close',
    budgetApprove: 'Budget Approve',
    download: 'Download',
    upload: 'Upload',
    share: 'Share',
    archive: 'Archive',
  },
  
  // Status
  status: {
    success: 'Success',
    failed: 'Failed',
    partial: 'Partial',
    pending: 'Pending',
  },
  
  // Risk Level
  riskLevel: {
    low: 'Low Risk',
    medium: 'Medium Risk',
    high: 'High Risk',
  },
  
  // Module Names
  module: {
    user: 'User Management',
    role: 'Role Management',
    permission: 'Permission Management',
    department: 'Department Management',
    position: 'Position Management',
    form: 'Form Management',
    approval: 'Approval Process',
    workRecord: 'Work Records',
    part: 'Parts Management',
    inventory: 'Inventory Management',
    ticket: 'Ticket System',
    notification: 'Notification Management',
    auth: 'Authentication',
    system: 'System Settings',
  },
  
  // Common
  loading: 'Loading...',
  loadFailed: 'Load Failed',
  retry: 'Retry',
  verifyIntegrity: 'Verify',
  analysis: 'Analysis',
  admin: 'Admin',
  
  // Empty States
  empty: {
    noData: 'No data',
    noLogs: 'No audit logs',
    noResults: 'No matching records found',
  },

  // User Guide
  help: {
    title: 'User Guide',
    subtitle: 'Quick overview of the audit system',
    nav: 'User Guide',
    intro: {
      title: 'What is this',
      paragraph:
        'The audit system records the "Who / What / When / Where / Why / How" (5W1H) of every important operation. A SHA-256 hash chain protects logs from tampering, and financial operations are retained for 7 years to meet SOX compliance. Developers add an @Auditable decorator instead of writing logging code by hand.',
      highlights: [
        { title: 'Full 5W1H', desc: 'Captures actor / action / timestamp / IP / reason / origin per record' },
        { title: 'Tamper-proof', desc: 'SHA-256 hash chain + DB triggers blocking UPDATE/DELETE; single-record verification on demand' },
        { title: 'SOX-ready', desc: 'Financial ops retained 7 years; SOX / GDPR reports generated automatically' },
      ],
    },
    concepts: {
      title: 'Core Concepts',
      description: 'These few terms make everything else clearer',
      items: [
        { term: '5W1H', desc: 'The full anatomy of an audit record: Who / What / When / Where (IP + device) / Why / How (API/UI/CLI/system)' },
        { term: '@Auditable', desc: 'Backend decorator: place it on a method and that method call is automatically logged as an audit event' },
        { term: '@Sensitive', desc: 'Marks a sensitive operation; routed to the sensitive-log table and surfaced on the "Sensitive Operations" page' },
        { term: '@Financial', desc: 'Marks a financial operation; triggers the 7-year retention policy and shows in "Financial Audit"' },
        { term: 'Hash chain', desc: 'Each log\'s hash includes the previous log\'s hash. Tampering with any single record breaks every later verification' },
        { term: 'traceId', desc: 'Request-scope trace ID, used to correlate multiple audit records produced by the same user action' },
        { term: 'businessKey', desc: 'Business reference (approval / finance / form ID). The detail page links straight back to the origin record' },
      ],
    },
    roles: {
      title: 'Roles & Visibility',
      description: 'Each role sees a different slice of audit data',
      columnHeaders: {
        role: 'Role',
        scope: 'Scope',
        desc: 'Description',
      },
      items: [
        { code: 'Administrator', name: 'System Admin', scope: 'All', desc: 'Reads every audit log (incl. financial & sensitive); can run integrity checks' },
        { code: 'AuditAdmin', name: 'Audit Admin', scope: 'All', desc: 'Dedicated audit role; same read scope as System Admin' },
        { code: 'FinanceAuditor', name: 'Finance Auditor', scope: 'Financial logs', desc: 'Sees the Financial Audit page — amounts, contracts, payments' },
        { code: 'SensitiveAuditor', name: 'Security Auditor', scope: 'Sensitive logs', desc: 'Sees the Sensitive Operations page — permission changes, exports, super-user actions' },
        { code: 'Business User', name: 'Business User', scope: 'Own actions', desc: 'Sees only logs produced by themselves (gated by the audit:read permission)' },
      ],
    },
    tasks: {
      title: 'Common Tasks',
      description: 'Click a card to jump into the corresponding page',
      cards: [
        { id: 'overview', name: 'Audit Overview', desc: 'Last-7-day operation totals, failure trends, module breakdown' },
        { id: 'logs', name: 'Audit Log List', desc: 'Browse all audit records in reverse-chronological order with module / type filters' },
        { id: 'search', name: 'Advanced Search', desc: 'Precise lookup by user / entity / time / business reference' },
        { id: 'integrity', name: 'Integrity Verification', desc: 'Verify the audit hash chain has not been tampered with' },
        { id: 'financial', name: 'Financial Audit', desc: 'Dedicated query for amount / contract / payment operations' },
        { id: 'sensitive', name: 'Sensitive Operations', desc: 'Monitor permission changes, super-user actions, data exports' },
        { id: 'statistics', name: 'Statistics', desc: 'Charts grouped by operation type / trend / module' },
      ],
    },
  },
};
